import { useQuery } from "@apollo/client/react";
import type { VideoPreviewPlayback } from "@athena/ui";
import { VideoPlaybackForAssetDocument } from "@/graphql/media.generated";

/**
 * Playback for a Library asset, shaped for `VideoPreviewDialog`.
 *
 * Addressed by asset rather than by content: an upload need not be attached to any lesson, and
 * `videoPlayback` runs a learner eligibility layer that has no meaning in the admin surfaces.
 *
 * Pass null to fetch nothing — the dialog is closed.
 */
export function useVideoPlayback(assetId: string | null): {
    playback: VideoPreviewPlayback | null;
    loading: boolean;
    errorMessage: string | null;
} {
    const { data, loading, error } = useQuery(VideoPlaybackForAssetDocument, {
        variables: { videoAssetId: assetId ?? "" },
        skip: !assetId,
        // The manifest URL is signed and expires, so a cached one can be dead on reopen.
        fetchPolicy: "network-only",
    });

    const playback = data?.videoPlaybackForAsset;

    return {
        playback: playback
            ? {
                  manifestUrl: playback.manifestUrl,
                  posterUrl: playback.posterUrl,
                  captions: playback.captions.map((c) => ({ language: c.language, src: c.vttUrl })),
              }
            : null,
        loading,
        errorMessage: error?.message ?? null,
    };
}
